import React, { useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import Layout from "../../layout/Layout";
import { compareInvoiceDocumentsApi } from "../../../api/muhasebe/earsivocr/earsiv";

const INITIAL_OPTIONS = { debug: false };

const DOCS = [
  { key: "excel", label: "Excel" },
  { key: "eInvoice", label: "e-Fatura" },
  { key: "commercialInvoice", label: "Commercial Invoice" },
];

const GROUPS = [
  {
    key: "threeWayChecks",
    title: "3’lü Ortak Kontroller",
    description:
      "Excel, e-Fatura ve Commercial Invoice üzerinde ortak kontrol edilen alanlar.",
    docs: ["excel", "eInvoice", "commercialInvoice"],
    pairKey: null,
  },
  {
    key: "excelVsCommercial",
    title: "Excel ↔ Commercial Invoice",
    description:
      "Sadece Excel ile Commercial Invoice arasında kontrol edilen alanlar.",
    docs: ["excel", "commercialInvoice"],
    pairKey: "excelVsCommercialInvoice",
  },
  {
    key: "excelVsEInvoice",
    title: "Excel ↔ e-Fatura",
    description: "Sadece Excel ile e-Fatura arasında kontrol edilen alanlar.",
    docs: ["excel", "eInvoice"],
    pairKey: "excelVsEInvoice",
  },
  {
    key: "eInvoiceVsCommercial",
    title: "e-Fatura ↔ Commercial Invoice",
    description:
      "Sadece e-Fatura ile Commercial Invoice arasında kontrol edilen alanlar.",
    docs: ["eInvoice", "commercialInvoice"],
    pairKey: "eInvoiceVsCommercialInvoice",
  },
];

const FIELD_ALIASES = {
  documentNumber: "invoiceNo",
  documentDate: "invoiceDate",
  origin: "originCountry",
  accountNo: "bankAccountNo",
  totalPackageCount: "packageCount",
  totalGrossWeight: "grossWeight",
  totalFobAmount: "fobAmount",
  diibDocumentNumber: "diibDocumentNo",
  branchCode: "bankBranchCode",

  // Eski response uyumluluğu:
  // goodsDescription yalnızca X/Y malzeme adı seviyesinde fallback olarak kullanılır.
  goodsDescription: "materialName",
};

const GROUP_FIELD_KEYS = {
  threeWayChecks: new Set([
    "sellerName",
    "buyerName",
    "buyerAddress",
    "invoiceNo",
    "invoiceDate",
    "currency",
    "deliveryTerm",
    "originCountry",
    "totalNetWeight",
    "totalAmount",
    "bankName",
    "bankAccountNo",
    "iban",

    // Bu üç alan backend'de gruplama/normalizasyon tamamlandığında
    // üçlü kontrol olarak dönebilir.
    "lineQuantity",
    "unitPrice",
    "normalizedUnitPrice",
    "lineAmount",
  ]),

  excelVsCommercial: new Set(["dimension", "freightAmount", "fobAmount"]),

  excelVsEInvoice: new Set([
    "materialName",
    "gtip",
    "packageCount",
    "dispatchNo",
    "grossWeight",
    "diibDocumentNo",
    "diibLineCode",
    "diibGtipAllocation",
  ]),

  eInvoiceVsCommercial: new Set([
    "swiftCode",
    "bankBranchCode",
    "bankBranchName",
    "sellerAddress",
    "sellerPhone",
    "sellerWebsite",
  ]),
};

const SEMANTICALLY_DISABLED_KEYS = new Set([
  "paymentMethod",
  "productDescription",
  "buyerTaxIdentifier",
]);

const PRIORITY = [
  "invoiceNo",
  "documentNumber",
  "invoiceDate",
  "documentDate",
  "buyerName",
  "buyerAddress",
  "deliveryTerm",
  "originCountry",
  "origin",
  "totalNetWeight",
  "packageCount",
  "totalPackageCount",
  "totalAmount",
  "bankName",
  "bankAccountNo",
  "accountNo",
  "iban",
  "normalizedUnitPrice",
  "dimension",
  "materialName",
  "goodsDescription",
  "gtip",
  "dispatchNo",
  "grossWeight",
  "totalGrossWeight",
  "diibDocumentNo",
  "diibDocumentNumber",
  "diibLineCode",
  "swiftCode",
  "bankBranchCode",
  "branchCode",
  "bankBranchName",
];

const DISABLED_REASON = "Bu alan şu an kontrol dışında.";

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const docLabel = (key) => DOCS.find((item) => item.key === key)?.label || key;

const fieldKey = (field) => field?.field || field?.key || field?.name || "";

const canonicalFieldKey = (field) => {
  const key = fieldKey(field);
  return FIELD_ALIASES[key] || key;
};

const fieldLabel = (field) =>
  field?.label ||
  field?.fieldLabel ||
  field?.title ||
  fieldKey(field) ||
  "Alan";

function getErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "İşlem sırasında beklenmeyen bir hata oluştu."
  );
}

function formatNumber(value, digits = 4) {
  if (!hasValue(value)) return "-";

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat("tr-TR", {
    maximumFractionDigits: digits,
  }).format(number);
}

function formatMoney(value, currency = "USD") {
  if (!hasValue(value)) return "-";

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(number);
  } catch {
    return `${formatNumber(number)} ${currency}`.trim();
  }
}

function renderFieldValue(value) {
  if (!hasValue(value)) return "-";

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => renderFieldValue(item))
      .filter((item) => item !== "-");

    return parts.length ? parts.join(" / ") : "-";
  }

  if (typeof value === "object") {
    const preferredKeys = [
      "displayValue",
      "raw",
      "normalizedValue",
      "normalized",
      "value",
      "text",
      "label",
      "name",
      "description",
      "code",
      "number",
      "amount",
    ];

    for (const key of preferredKeys) {
      if (hasValue(value?.[key])) {
        return renderFieldValue(value[key]);
      }
    }

    const meaningfulParts = Object.entries(value)
      .filter(([, childValue]) => hasValue(childValue))
      .slice(0, 4)
      .map(([key, childValue]) => {
        const rendered = renderFieldValue(childValue);
        return rendered === "-" ? null : `${key}: ${rendered}`;
      })
      .filter(Boolean);

    return meaningfulParts.length ? meaningfulParts.join(" / ") : "-";
  }

  return String(value);
}

function formatText(value) {
  return renderFieldValue(value);
}

function formatFileSize(file) {
  if (!file) return "-";
  const kb = file.size / 1024;
  return kb >= 1024
    ? `${formatNumber(kb / 1024, 2)} MB`
    : `${formatNumber(kb, 2)} KB`;
}

function unwrapValue(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  if (hasValue(value.raw)) return value.raw;
  if (hasValue(value.value)) return value.value;
  if (hasValue(value.amount)) return value.amount;
  return value;
}

function rawValue(field, docKey) {
  if (
    field?.rawValues &&
    Object.prototype.hasOwnProperty.call(field.rawValues, docKey)
  ) {
    return unwrapValue(field.rawValues[docKey]);
  }

  const value = field?.values?.[docKey];

  if (value && typeof value === "object" && hasValue(value.raw)) {
    return value.raw;
  }

  return unwrapValue(value);
}

function normalizedValue(field, docKey) {
  if (
    field?.normalizedValues &&
    Object.prototype.hasOwnProperty.call(field.normalizedValues, docKey)
  ) {
    return unwrapValue(field.normalizedValues[docKey]);
  }

  if (
    field?.normalized &&
    Object.prototype.hasOwnProperty.call(field.normalized, docKey)
  ) {
    return unwrapValue(field.normalized[docKey]);
  }

  const value = field?.values?.[docKey];

  if (value && typeof value === "object") {
    if (hasValue(value.normalized)) return value.normalized;
    if (hasValue(value.normalizedValue)) return value.normalizedValue;
  }

  return null;
}

function sameDisplayValue(a, b) {
  if (!hasValue(a) || !hasValue(b)) return false;

  return (
    renderFieldValue(a).trim().toLocaleLowerCase("tr-TR") ===
    renderFieldValue(b).trim().toLocaleLowerCase("tr-TR")
  );
}

function formatFieldValue(field, value, document) {
  if (!hasValue(value)) return "-";

  const key = fieldKey(field);

  if (
    [
      "totalAmount",
      "totalFobAmount",
      "lineAmount",
      "normalizedUnitPrice",
      "unitPrice",
    ].includes(key)
  ) {
    return formatMoney(value, document?.currency || "USD");
  }

  if (field?.type === "NUMBER" || typeof value === "number") {
    return formatNumber(value);
  }

  return formatText(value);
}

function normalizeStatus(value) {
  if (value === true) return "MATCHED";
  if (value === false) return "DIFFERENT";

  const status = String(value || "")
    .trim()
    .toUpperCase();

  if (["MATCHED", "MATCH", "EQUAL", "SAME", "OK", "SUCCESS"].includes(status)) {
    return "MATCHED";
  }

  if (
    ["DIFFERENT", "DIFF", "NOT_MATCHED", "MISMATCH", "FAIL", "FAILED"].includes(
      status
    )
  ) {
    return "DIFFERENT";
  }

  if (
    [
      "MISSING",
      "NOT_FOUND",
      "UNREADABLE",
      "NOT_COMPARABLE",
      "NO_VALUE",
      "EMPTY",
    ].includes(status)
  ) {
    return "MISSING";
  }

  if (
    ["SKIPPED", "NOT_APPLICABLE", "N/A", "NA", "DISABLED", "IGNORED"].includes(
      status
    )
  ) {
    return "SKIPPED";
  }

  return null;
}

function getFieldGroup(field) {
  return GROUPS.find((group) => group.key === field?.group) || null;
}

function fieldHasMissingDocumentValue(field, group) {
  return group.docs.some((docKey) => !hasValue(rawValue(field, docKey)));
}

function getFieldStatus(field) {
  const group = getFieldGroup(field);

  if (!group) {
    return "SKIPPED";
  }

  // Kritik güvenlik kuralı:
  // Bir belgede değer yoksa backend yanlışlıkla MATCHED dese bile yeşil gösterme.
  if (fieldHasMissingDocumentValue(field, group)) {
    return "MISSING";
  }

  const backendStatus =
    normalizeStatus(field?.status) ||
    normalizeStatus(field?.result) ||
    normalizeStatus(field?.comparisonStatus) ||
    normalizeStatus(field?.matchStatus);

  if (backendStatus) {
    return backendStatus;
  }

  if (field?.isMatched === false || field?.matched === false) {
    return "DIFFERENT";
  }

  if (field?.isMatched === true || field?.matched === true) {
    return "MATCHED";
  }

  if (group.key === "threeWayChecks") {
    const pairKeys = [
      "excelVsEInvoice",
      "excelVsCommercialInvoice",
      "eInvoiceVsCommercialInvoice",
    ];

    const pairResults = pairKeys
      .map((key) => field?.[key])
      .filter((value) => value === true || value === false);

    // Bu grup için hiçbir pair sonucu yoksa frontend tahmin üretmez.
    if (!pairResults.length) {
      return "SKIPPED";
    }

    if (pairResults.some((value) => value === false)) {
      return "DIFFERENT";
    }

    if (pairResults.every((value) => value === true)) {
      return "MATCHED";
    }

    return "SKIPPED";
  }

  if (!group.pairKey) {
    return "SKIPPED";
  }

  const pairResult = field?.[group.pairKey];

  if (pairResult === true) {
    return "MATCHED";
  }

  if (pairResult === false) {
    return "DIFFERENT";
  }

  return "SKIPPED";
}

function withGroup(fields = [], groupKey) {
  return fields.map((field) => ({
    ...field,
    group: field?.group || groupKey,
  }));
}

function getCanonicalGroupKey(field) {
  const canonicalKey = canonicalFieldKey(field);

  for (const group of GROUPS) {
    if (GROUP_FIELD_KEYS[group.key]?.has(canonicalKey)) {
      return group.key;
    }
  }

  return null;
}

function getLegacyPairFallbackGroup(field) {
  const pairMap = [
    {
      groupKey: "excelVsCommercial",
      pairKey: "excelVsCommercialInvoice",
    },
    {
      groupKey: "excelVsEInvoice",
      pairKey: "excelVsEInvoice",
    },
    {
      groupKey: "eInvoiceVsCommercial",
      pairKey: "eInvoiceVsCommercialInvoice",
    },
  ];

  const validPairs = pairMap.filter(
    ({ pairKey }) => field?.[pairKey] === true || field?.[pairKey] === false
  );

  if (validPairs.length === 3) {
    return "threeWayChecks";
  }

  if (validPairs.length === 1) {
    return validPairs[0].groupKey;
  }

  return null;
}

function fallbackGroups(fields = []) {
  const result = {
    threeWayChecks: { fields: [] },
    excelVsCommercial: { fields: [] },
    excelVsEInvoice: { fields: [] },
    eInvoiceVsCommercial: { fields: [] },
  };

  fields.forEach((field) => {
    const canonicalKey = canonicalFieldKey(field);

    // Semantik olarak birebir equality yapılmaması gereken alanları
    // frontend aktif gruba zorla taşımaz.
    if (SEMANTICALLY_DISABLED_KEYS.has(canonicalKey)) {
      return;
    }

    // Öncelik canonical iş kapsamıdır.
    const canonicalGroupKey = getCanonicalGroupKey(field);

    if (canonicalGroupKey) {
      result[canonicalGroupKey].fields.push({
        ...field,
        group: canonicalGroupKey,
      });

      return;
    }

    // Eski backend response'u için yalnızca bilinmeyen alanlarda
    // pair sonuçlarından uyumluluk fallback'i uygulanır.
    const legacyGroupKey = getLegacyPairFallbackGroup(field);

    if (legacyGroupKey) {
      result[legacyGroupKey].fields.push({
        ...field,
        group: legacyGroupKey,
      });
    }
  });

  return result;
}

function resolveGroups(comparison) {
  const groups = comparison?.groups;

  // Yeni backend sözleşmesi:
  // group + values + normalizedValues + status geldiğinde
  // frontend gruplama/eşitlik yorumu yapmaz, sadece gösterir.
  if (
    groups &&
    (groups?.threeWayChecks ||
      groups?.excelVsCommercial ||
      groups?.excelVsEInvoice ||
      groups?.eInvoiceVsCommercial)
  ) {
    return {
      threeWayChecks: {
        ...groups?.threeWayChecks,
        fields: withGroup(
          groups?.threeWayChecks?.fields || [],
          "threeWayChecks"
        ),
      },

      excelVsCommercial: {
        ...groups?.excelVsCommercial,
        fields: withGroup(
          groups?.excelVsCommercial?.fields || [],
          "excelVsCommercial"
        ),
      },

      excelVsEInvoice: {
        ...groups?.excelVsEInvoice,
        fields: withGroup(
          groups?.excelVsEInvoice?.fields || [],
          "excelVsEInvoice"
        ),
      },

      eInvoiceVsCommercial: {
        ...groups?.eInvoiceVsCommercial,
        fields: withGroup(
          groups?.eInvoiceVsCommercial?.fields || [],
          "eInvoiceVsCommercial"
        ),
      },
    };
  }

  return fallbackGroups(comparison?.fields || []);
}

function sortFields(fields = []) {
  const indexes = new Map(PRIORITY.map((key, index) => [key, index]));

  return [...fields].sort((a, b) => {
    const aIndex = indexes.has(fieldKey(a)) ? indexes.get(fieldKey(a)) : 9999;
    const bIndex = indexes.has(fieldKey(b)) ? indexes.get(fieldKey(b)) : 9999;

    if (aIndex !== bIndex) return aIndex - bIndex;

    return fieldLabel(a).localeCompare(fieldLabel(b), "tr");
  });
}

function statsForGroup(fields) {
  const stats = {
    total: fields.length,
    matchedCount: 0,
    differentCount: 0,
    missingCount: 0,
    skippedCount: 0,
  };

  fields.forEach((field) => {
    const status = getFieldStatus(field);

    if (status === "MATCHED") {
      stats.matchedCount += 1;
    } else if (status === "DIFFERENT") {
      stats.differentCount += 1;
    } else if (status === "MISSING") {
      stats.missingCount += 1;
    } else {
      stats.skippedCount += 1;
    }
  });

  return stats;
}

function StatusBadge({ status }) {
  if (status === "MATCHED") {
    return <Badge bg="success">Eşleşti</Badge>;
  }

  if (status === "DIFFERENT") {
    return <Badge bg="danger">Farklı</Badge>;
  }

  if (status === "MISSING") {
    return (
      <Badge bg="warning" text="dark">
        Eksik Veri
      </Badge>
    );
  }

  return <Badge bg="secondary">Atlandı / Uygulanmaz</Badge>;
}

function SummaryCard({ label, value, variant }) {
  const textClass =
    variant === "success"
      ? "text-success"
      : variant === "danger"
        ? "text-danger"
        : variant === "warning"
          ? "text-warning"
          : "";

  return (
    <Col xs={6} xl={3}>
      <div className="border rounded p-3 bg-light h-100">
        <div className="small text-muted">{label}</div>
        <div className={`fs-4 fw-bold ${textClass}`}>{value}</div>
      </div>
    </Col>
  );
}

function UploadCard({
  title,
  description,
  file,
  inputId,
  accept,
  disabled,
  onChange,
}) {
  return (
    <Card className={`h-100 ${file ? "border-success" : ""}`}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span className="fw-semibold">{title}</span>
        <Badge bg={file ? "success" : "danger"}>
          {file ? "Hazır" : "Zorunlu"}
        </Badge>
      </Card.Header>

      <Card.Body>
        <div className="small text-muted mb-3">{description}</div>

        <Form.Control
          id={inputId}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={onChange}
        />

        <div className="mt-3 border rounded p-3 bg-light">
          {file ? (
            <>
              <div className="fw-semibold text-break">{file.name}</div>
              <div className="small text-muted mt-1">
                {formatFileSize(file)}
              </div>
            </>
          ) : (
            <div className="small text-muted">Dosya seçilmedi.</div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function DocumentCard({ title, document }) {
  if (!document) {
    return (
      <Card className="h-100">
        <Card.Header className="fw-semibold">{title}</Card.Header>
        <Card.Body className="text-muted">Belge bilgisi bulunamadı.</Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center gap-2">
        <span className="fw-semibold">{title}</span>
        <Badge bg="light" text="dark">
          {formatText(document.sourceType)}
        </Badge>
      </Card.Header>

      <Card.Body>
        <div className="mb-3">
          <div className="small text-muted">Dosya adı</div>
          <div className="fw-semibold text-break">
            {formatText(document.sourceFile)}
          </div>
        </div>

        <Row className="g-3">
          <Col md={6}>
            <div className="small text-muted">Belge no</div>
            <div className="fw-semibold text-break">
              {formatText(document.documentNumber || document.invoiceNo)}
            </div>
          </Col>

          <Col md={6}>
            <div className="small text-muted">Tarih</div>
            <div className="fw-semibold">
              {formatText(document.documentDate || document.invoiceDate)}
            </div>
          </Col>

          <Col md={6}>
            <div className="small text-muted">Toplam tutar</div>
            <div className="fw-semibold">
              {formatMoney(document.totalAmount, document.currency || "USD")}
            </div>
          </Col>

          <Col md={6}>
            <div className="small text-muted">Para birimi</div>
            <div className="fw-semibold">{formatText(document.currency)}</div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function ValueCell({ field, docKey, document }) {
  const raw = rawValue(field, docKey);
  const normalized = normalizedValue(field, docKey);

  return (
    <div style={{ minWidth: 190 }}>
      <div className="fw-semibold text-break">
        {formatFieldValue(field, raw, document)}
      </div>

      {hasValue(normalized) && !sameDisplayValue(raw, normalized) ? (
        <div className="small text-muted mt-1 text-break">
          Normalize: {formatFieldValue(field, normalized, document)}
        </div>
      ) : null}
    </div>
  );
}

function ResultTable({ group, fields, documents, filter }) {
  const visibleFields = sortFields(fields).filter(
    (field) => filter === "ALL" || getFieldStatus(field) === filter
  );

  if (!visibleFields.length) {
    return (
      <Alert variant="light" className="border m-3">
        Seçilen filtre için sonuç bulunamadı.
      </Alert>
    );
  }

  return (
    <div className="table-responsive">
      <Table bordered hover className="mb-0 align-middle">
        <thead>
          <tr>
            <th style={{ minWidth: 180 }}>Alan</th>

            {group.docs.map((docKey) => (
              <th key={docKey} style={{ minWidth: 220 }}>
                {docLabel(docKey)}
              </th>
            ))}

            <th style={{ minWidth: 170 }}>Sonuç</th>
          </tr>
        </thead>

        <tbody>
          {visibleFields.map((field, index) => {
            const status = getFieldStatus(field);

            const rowClass =
              status === "DIFFERENT"
                ? "table-danger"
                : status === "MATCHED"
                  ? "table-success"
                  : status === "MISSING"
                    ? "table-warning"
                    : "";

            return (
              <tr
                key={`${group.key}-${fieldKey(field)}-${index}`}
                className={rowClass}
              >
                <td>
                  <div className="fw-semibold">{fieldLabel(field)}</div>
                  <div className="small text-muted">{fieldKey(field)}</div>

                  {hasValue(field?.tolerance) ? (
                    <div className="small text-muted mt-1">
                      Tolerans: ±{formatNumber(field.tolerance, 4)}
                    </div>
                  ) : null}
                </td>

                {group.docs.map((docKey) => (
                  <td key={docKey}>
                    <ValueCell
                      field={field}
                      docKey={docKey}
                      document={documents?.[docKey]}
                    />
                  </td>
                ))}

                <td>
                  <StatusBadge status={status} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

function getGroupNote(groupKey) {
  if (groupKey === "threeWayChecks") {
    return "Ortak alan olması tek başına eşitlik anlamına gelmez; normalize edilmiş backend status sonucu gösterilir.";
  }

  if (groupKey === "excelVsCommercial") {
    return "EBAT, navlun ve FOB kontrolleri bu gruptadır.";
  }

  if (groupKey === "excelVsEInvoice") {
    return "Malzeme, GTİP, paket/kap, irsaliye, brüt kilo ve DİİB kontrolleri bu gruptadır.";
  }

  if (groupKey === "eInvoiceVsCommercial") {
    return "SWIFT, banka şube ve satıcı iletişim bilgileri bu gruptadır.";
  }

  return "";
}

function GroupCard({ group, data, documents }) {
  const [filter, setFilter] = useState("ALL");
  const fields = data?.fields || [];

  const stats = useMemo(() => statsForGroup(fields), [fields, group]);

  return (
    <Accordion.Item
      eventKey={group.key}
      className="mb-3 border rounded overflow-hidden"
    >
      <Accordion.Header>
        <div className="w-100 pe-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <div className="fw-semibold">{group.title}</div>
              <div className="small text-muted">{group.description}</div>
              <div className="small mt-1">
                <span className="text-success">
                  {stats.matchedCount} eşleşen
                </span>
                {" · "}
                <span className="text-danger">
                  {stats.differentCount} farklı
                </span>
                {" · "}
                <span className="text-warning">{stats.missingCount} eksik</span>
                {stats.skippedCount > 0 ? (
                  <>
                    {" · "}
                    <span className="text-muted">
                      {stats.skippedCount} atlanan
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            <div className="d-flex gap-2">
              <Badge bg="light" text="dark">
                {stats.total} alan
              </Badge>

              {stats.differentCount > 0 ? (
                <Badge bg="danger">{stats.differentCount} farklı</Badge>
              ) : stats.missingCount > 0 ? (
                <Badge bg="warning" text="dark">
                  {stats.missingCount} eksik
                </Badge>
              ) : stats.matchedCount > 0 ? (
                <Badge bg="success">Fark Yok</Badge>
              ) : (
                <Badge bg="secondary">Kontrol Yok</Badge>
              )}
            </div>
          </div>
        </div>
      </Accordion.Header>

      <Accordion.Body className="p-0">
        <div className="p-3 border-bottom bg-light">
          <div className="small text-muted mb-3">{getGroupNote(group.key)}</div>

          <Row className="g-3 mb-3">
            <Col xs={6} md>
              <div className="small text-muted">Toplam</div>
              <div className="fw-bold">{stats.total}</div>
            </Col>

            <Col xs={6} md>
              <div className="small text-muted">Eşleşen</div>
              <div className="fw-bold text-success">{stats.matchedCount}</div>
            </Col>

            <Col xs={6} md>
              <div className="small text-muted">Farklı</div>
              <div className="fw-bold text-danger">{stats.differentCount}</div>
            </Col>

            <Col xs={6} md>
              <div className="small text-muted">Eksik</div>
              <div className="fw-bold text-warning">{stats.missingCount}</div>
            </Col>

            <Col xs={6} md>
              <div className="small text-muted">Atlanan</div>
              <div className="fw-bold text-secondary">{stats.skippedCount}</div>
            </Col>
          </Row>

          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={filter === "ALL" ? "primary" : "outline-primary"}
              onClick={() => setFilter("ALL")}
            >
              Tümü ({stats.total})
            </Button>

            <Button
              size="sm"
              variant={filter === "DIFFERENT" ? "danger" : "outline-danger"}
              onClick={() => setFilter("DIFFERENT")}
            >
              Sadece farklı ({stats.differentCount})
            </Button>

            <Button
              size="sm"
              variant={filter === "MATCHED" ? "success" : "outline-success"}
              onClick={() => setFilter("MATCHED")}
            >
              Sadece eşleşen ({stats.matchedCount})
            </Button>

            <Button
              size="sm"
              variant={filter === "MISSING" ? "warning" : "outline-warning"}
              onClick={() => setFilter("MISSING")}
            >
              Eksik veriler ({stats.missingCount})
            </Button>

            {stats.skippedCount > 0 ? (
              <Button
                size="sm"
                variant={
                  filter === "SKIPPED" ? "secondary" : "outline-secondary"
                }
                onClick={() => setFilter("SKIPPED")}
              >
                Atlananlar ({stats.skippedCount})
              </Button>
            ) : null}
          </div>
        </div>

        <ResultTable
          group={group}
          fields={fields}
          documents={documents}
          filter={filter}
        />
      </Accordion.Body>
    </Accordion.Item>
  );
}

function getDisabledDocumentValue(field, document) {
  const key = fieldKey(field);

  if (!document || !key) {
    return null;
  }

  return document?.[key];
}

function disabledValuesDiffer(field, documents) {
  const values = DOCS.map((doc) =>
    getDisabledDocumentValue(field, documents?.[doc.key])
  )
    .filter(hasValue)
    .map((value) => renderFieldValue(value).trim().toLocaleLowerCase("tr-TR"));

  return new Set(values).size > 1;
}

function DisabledFieldsCard({ disabledFields, documents }) {
  const fields = disabledFields || [];

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Şu An Karşılaştırılmayan Alanlar</div>
          <div className="small text-muted">
            Backend bu alanları aktif equality kontrolüne sokmuyor. Değerler
            yine yan yana gösterilir. packageCount / totalPackageCount yeni
            sözleşmede Excel ↔ e-Fatura grubuna taşınmalıdır.
          </div>
        </div>

        <Badge bg="secondary">{fields.length} alan</Badge>
      </Card.Header>

      <div className="table-responsive">
        <Table bordered hover size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ minWidth: 210 }}>Alan</th>
              <th style={{ minWidth: 160 }}>Excel</th>
              <th style={{ minWidth: 160 }}>e-Fatura</th>
              <th style={{ minWidth: 160 }}>Commercial</th>
              <th style={{ minWidth: 190 }}>Durum</th>
              <th style={{ minWidth: 260 }}>Sebep</th>
            </tr>
          </thead>

          <tbody>
            {fields.length ? (
              fields.map((field, index) => {
                const valuesDifferent = disabledValuesDiffer(field, documents);

                return (
                  <tr key={`disabled-${fieldKey(field)}-${index}`}>
                    <td>
                      <div className="fw-semibold">{fieldLabel(field)}</div>
                      <div className="small text-muted">{fieldKey(field)}</div>
                    </td>

                    {DOCS.map((doc) => (
                      <td key={doc.key} className="text-break">
                        {renderFieldValue(
                          getDisabledDocumentValue(field, documents?.[doc.key])
                        )}
                      </td>
                    ))}

                    <td>
                      {["packageCount", "totalPackageCount"].includes(
                        fieldKey(field)
                      ) ? (
                        <div className="d-flex flex-column gap-1">
                          {valuesDifferent ? (
                            <Badge bg="warning" text="dark">
                              Değerler farklı
                            </Badge>
                          ) : (
                            <Badge bg="secondary">Kontrol dışı</Badge>
                          )}

                          <span className="small text-muted">
                            Hedef grup: Excel ↔ e-Fatura
                          </span>
                        </div>
                      ) : valuesDifferent ? (
                        <Badge bg="warning" text="dark">
                          Değerler farklı — kontrol dışı
                        </Badge>
                      ) : (
                        <Badge bg="secondary">Kontrol dışı</Badge>
                      )}
                    </td>

                    <td className="text-muted">
                      {field?.reason || field?.description || DISABLED_REASON}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  Karşılaştırma dışı alan bulunmuyor.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Card>
  );
}

function ResultScreen({ comparison, documents }) {
  const groups = useMemo(() => resolveGroups(comparison), [comparison]);

  const disabledFields = comparison?.config?.disabledFields || [];
  const summary = comparison?.summary || {};

  const calculated = useMemo(() => {
    const result = {
      total: 0,
      matched: 0,
      different: 0,
      missing: 0,
      skipped: 0,
    };

    GROUPS.forEach((group) => {
      const fields = groups?.[group.key]?.fields || [];
      const stats = statsForGroup(fields);

      result.total += stats.total;
      result.matched += stats.matchedCount;
      result.different += stats.differentCount;
      result.missing += stats.missingCount;
      result.skipped += stats.skippedCount;
    });

    return result;
  }, [groups]);

  const hasCalculatedRows = calculated.total > 0;

  const overallMatched = hasCalculatedRows
    ? calculated.different === 0 &&
      calculated.missing === 0 &&
      calculated.matched > 0
    : comparison?.isMatched === true ||
      String(comparison?.status || "").toUpperCase() === "MATCHED";

  const topSummary = {
    total: hasCalculatedRows
      ? calculated.total
      : summary.totalFields ?? summary.activeFieldCount ?? 0,

    matched: hasCalculatedRows ? calculated.matched : summary.matchedCount ?? 0,

    different: hasCalculatedRows
      ? calculated.different
      : summary.differentCount ?? 0,

    missing: hasCalculatedRows
      ? calculated.missing
      : summary.notComparableCount ??
        summary.notComparedCount ??
        summary.missingCount ??
        0,
  };

  const defaultOpenGroups = GROUPS.filter((group) => {
    const fields = groups?.[group.key]?.fields || [];
    const stats = statsForGroup(fields);

    return stats.differentCount > 0 || stats.missingCount > 0;
  }).map((group) => group.key);

  return (
    <>
      <Card
        className={`mb-3 ${
          overallMatched ? "border-success" : "border-danger"
        }`}
      >
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
            <div>
              <div className="small text-muted">Genel durum</div>

              <h4 className="mb-1">
                {overallMatched
                  ? "Belgeler Eşleşti"
                  : "Belgeler Arasında Fark Var"}
              </h4>

              <div className="text-muted">
                Aktif kontrol alanlarının genel karşılaştırma sonucu.
              </div>
            </div>

            <Badge
              bg={overallMatched ? "success" : "danger"}
              className="fs-6 px-3 py-2"
            >
              {overallMatched ? "EŞLEŞTİ" : "FARK VAR"}
            </Badge>
          </div>

          <Row className="g-3">
            <SummaryCard
              label="Toplam kontrol alanı"
              value={topSummary.total}
            />
            <SummaryCard
              label="Eşleşen"
              value={topSummary.matched}
              variant="success"
            />
            <SummaryCard
              label="Farklı"
              value={topSummary.different}
              variant="danger"
            />
            <SummaryCard
              label="Karşılaştırılamayan"
              value={topSummary.missing}
              variant="warning"
            />
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>
          <div className="fw-semibold">Belge Özetleri</div>
          <div className="small text-muted">
            Karşılaştırmaya giren üç belgenin temel bilgileri.
          </div>
        </Card.Header>

        <Card.Body>
          <Row className="g-3">
            <Col xl={4}>
              <DocumentCard title="Excel" document={documents?.excel} />
            </Col>

            <Col xl={4}>
              <DocumentCard title="e-Fatura" document={documents?.eInvoice} />
            </Col>

            <Col xl={4}>
              <DocumentCard
                title="Commercial Invoice"
                document={documents?.commercialInvoice}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <h5 className="mb-1">Kontrol Grupları</h5>
        <div className="text-muted mb-3">
          Fark bulunan gruplar varsayılan olarak açık gelir.
        </div>

        <Accordion
          alwaysOpen
          defaultActiveKey={
            defaultOpenGroups.length
              ? defaultOpenGroups
              : GROUPS.map((group) => group.key)
          }
        >
          {GROUPS.map((group) => (
            <GroupCard
              key={group.key}
              group={group}
              data={groups?.[group.key]}
              documents={documents}
            />
          ))}
        </Accordion>
      </div>

      <DisabledFieldsCard
        disabledFields={disabledFields}
        documents={documents}
      />
    </>
  );
}

export default function InvoiceComparison() {
  const [excelFile, setExcelFile] = useState(null);
  const [invoicePdf, setInvoicePdf] = useState(null);
  const [commercialInvoicePdf, setCommercialInvoicePdf] = useState(null);

  const [options, setOptions] = useState(INITIAL_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [comparison, setComparison] = useState(null);
  const [documents, setDocuments] = useState(null);

  const selectedFileCount = [
    excelFile,
    invoicePdf,
    commercialInvoicePdf,
  ].filter(Boolean).length;

  const allFilesSelected = Boolean(
    excelFile && invoicePdf && commercialInvoicePdf
  );

  const canCompare = allFilesSelected && !isLoading;

  const resetResult = () => {
    setComparison(null);
    setDocuments(null);
    setError("");
    setMessage("");
    setUploadProgress(0);
  };

  const handleUploadProgress = (progressEvent) => {
    if (!progressEvent.total) return;

    const percentage = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );

    setUploadProgress(Math.min(percentage, 100));
  };

  const handleCompare = async () => {
    if (!excelFile) {
      setError("Excel dosyası zorunludur.");
      return;
    }

    if (!invoicePdf) {
      setError("e-Fatura PDF dosyası zorunludur.");
      return;
    }

    if (!commercialInvoicePdf) {
      setError("Commercial Invoice PDF dosyası zorunludur.");
      return;
    }

    setError("");
    setMessage("");
    setComparison(null);
    setDocuments(null);
    setUploadProgress(0);
    setIsLoading(true);

    try {
      const response = await compareInvoiceDocumentsApi(
        invoicePdf,
        excelFile,
        commercialInvoicePdf,
        {
          debug: options.debug,
          onUploadProgress: handleUploadProgress,
        }
      );

      const data = response?.data?.data || {};

      setComparison(data.comparison || null);
      setDocuments(data.documents || null);

      setMessage(response?.data?.message || "Karşılaştırma tamamlandı.");
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setExcelFile(null);
    setInvoicePdf(null);
    setCommercialInvoicePdf(null);

    setOptions(INITIAL_OPTIONS);
    setIsLoading(false);
    setUploadProgress(0);

    setError("");
    setMessage("");

    setComparison(null);
    setDocuments(null);

    [
      "comparison-excel-file",
      "comparison-invoice-pdf",
      "comparison-commercial-pdf",
    ].forEach((id) => {
      const input = document.getElementById(id);
      if (input) input.value = "";
    });
  };

  const processingTitle =
    uploadProgress < 100
      ? "Dosyalar yükleniyor..."
      : "Belgeler okunuyor ve alanlar karşılaştırılıyor...";

  const messageVariant =
    comparison?.isMatched === true
      ? "success"
      : comparison
        ? "warning"
        : "success";

  return (
    <Layout>
      <div className="container-fluid py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div>
            <h4 className="mb-1">Fatura Belge Karşılaştırma</h4>

            <div className="text-muted">
              Excel, e-Fatura ve Commercial Invoice belgelerini yükle,
              karşılaştır ve 4 kontrol grubunda sonucu incele.
            </div>
          </div>

          <Button
            variant="outline-secondary"
            disabled={isLoading}
            onClick={handleClear}
          >
            Tümünü Temizle
          </Button>
        </div>

        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {message ? (
          <Alert
            variant={messageVariant}
            dismissible
            onClose={() => setMessage("")}
          >
            {message}
          </Alert>
        ) : null}

        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center gap-2">
            <div>
              <div className="fw-semibold">Belgeleri Yükle</div>
              <div className="small text-muted">
                Üç dosyanın tamamı zorunludur.
              </div>
            </div>

            <Badge bg={selectedFileCount === 3 ? "success" : "secondary"}>
              {selectedFileCount}/3 dosya hazır
            </Badge>
          </Card.Header>

          <Card.Body>
            <Row className="g-3">
              <Col xl={4}>
                <UploadCard
                  title="Excel"
                  description="Excel fatura dosyasını seç."
                  file={excelFile}
                  inputId="comparison-excel-file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={isLoading}
                  onChange={(event) => {
                    setExcelFile(event.target.files?.[0] || null);
                    resetResult();
                  }}
                />
              </Col>

              <Col xl={4}>
                <UploadCard
                  title="e-Fatura PDF"
                  description="e-Fatura / e-Arşiv PDF dosyasını seç."
                  file={invoicePdf}
                  inputId="comparison-invoice-pdf"
                  accept=".pdf,application/pdf"
                  disabled={isLoading}
                  onChange={(event) => {
                    setInvoicePdf(event.target.files?.[0] || null);
                    resetResult();
                  }}
                />
              </Col>

              <Col xl={4}>
                <UploadCard
                  title="Commercial Invoice PDF"
                  description="Commercial Invoice PDF dosyasını seç."
                  file={commercialInvoicePdf}
                  inputId="comparison-commercial-pdf"
                  accept=".pdf,application/pdf"
                  disabled={isLoading}
                  onChange={(event) => {
                    setCommercialInvoicePdf(event.target.files?.[0] || null);
                    resetResult();
                  }}
                />
              </Col>
            </Row>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4">
              <Form.Check
                id="comparison-debug"
                type="switch"
                label="Debug modu"
                checked={options.debug}
                disabled={isLoading}
                onChange={(event) =>
                  setOptions((current) => ({
                    ...current,
                    debug: event.target.checked,
                  }))
                }
              />

              <Button
                variant="success"
                size="lg"
                disabled={!canCompare}
                onClick={handleCompare}
              >
                {isLoading ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : null}
                Karşılaştır
              </Button>
            </div>

            {!allFilesSelected ? (
              <div className="small text-muted text-end mt-2">
                Karşılaştırma için 3 dosyanın da seçilmesi gerekir.
              </div>
            ) : null}
          </Card.Body>
        </Card>

        {isLoading ? (
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />

                  <div>
                    <div className="fw-semibold">{processingTitle}</div>

                    <div className="small text-muted">
                      {uploadProgress < 100
                        ? "Dosyalar sunucuya gönderiliyor."
                        : "Yükleme tamamlandı. Sunucu belge içeriklerini analiz ediyor."}
                    </div>
                  </div>
                </div>

                <div className="fw-semibold">{uploadProgress}%</div>
              </div>

              <ProgressBar
                now={uploadProgress}
                label={`${uploadProgress}%`}
                animated={uploadProgress < 100}
              />

              {uploadProgress >= 100 ? (
                <div className="small mt-3">
                  <div className="text-success">✓ Dosyalar yüklendi</div>
                  <div className="text-muted">• Belgeler okunuyor...</div>
                  <div className="text-muted">
                    • Alanlar karşılaştırılıyor...
                  </div>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        ) : null}

        {comparison ? (
          <ResultScreen comparison={comparison} documents={documents} />
        ) : null}
      </div>
    </Layout>
  );
}
